import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useReactFlow } from 'reactflow';
import Y from 'yjs';
import { useAuthStore } from '../../../store/storeAuth';
import { stringToColor } from './utilis';

export type Cursor = {
  id: string;
  color: string;
  x: number;
  y: number;
  timestamp: number;
  userName: string;
};

const MAX_IDLE_TIME = 10000;
// Minimum movement (in flow units) before broadcasting a new cursor position.
const CURSOR_MIN_DELTA = 1;

// Broadcaster: writes this client's cursor on mouse move.
// No React state here, so the host (heavy) view does NOT re-render on mouse move.
export function useCursorBroadcaster(ydoc: Y.Doc) {
  const { screenToFlowPosition } = useReactFlow();
  const authUser = useAuthStore(state => state.user);

  const cursorsMap = ydoc.getMap<Cursor>('cursors');
  const cursorId = ydoc.clientID.toString();

  const rafRef = useRef(0);
  const lastClientRef = useRef<{ x: number; y: number } | null>(null);
  const lastSentRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (!authUser?.id) return;
      lastClientRef.current = { x: event.clientX, y: event.clientY };
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        const client = lastClientRef.current;
        if (!client) return;
        const position = screenToFlowPosition({ x: client.x, y: client.y });
        const last = lastSentRef.current;
        if (
          last &&
          Math.abs(position.x - last.x) < CURSOR_MIN_DELTA &&
          Math.abs(position.y - last.y) < CURSOR_MIN_DELTA
        ) {
          return;
        }
        lastSentRef.current = { x: position.x, y: position.y };
        const index = [...cursorsMap.values()].findIndex(
          ({ id }) => id === cursorId,
        );
        cursorsMap.set(cursorId, {
          id: cursorId,
          color: stringToColor(index >= 0 ? index : 0),
          x: position.x,
          y: position.y,
          timestamp: Date.now(),
          userName: authUser?.name,
        });
      });
    },
    [screenToFlowPosition, authUser?.id, authUser?.name, cursorsMap, cursorId],
  );
}

// Reader: subscribes to the cursors map. Call this INSIDE the small Cursors
// overlay so that only the overlay re-renders on cursor updates, not the view.
export function useCursorList(ydoc: Y.Doc) {
  const [cursors, setCursors] = useState<Cursor[]>([]);

  const cursorsMap = ydoc.getMap<Cursor>('cursors');
  const cursorId = ydoc.clientID.toString();

  const flush = useCallback(() => {
    const now = Date.now();
    for (const [id, cursor] of cursorsMap) {
      if (now - cursor.timestamp > MAX_IDLE_TIME) {
        cursorsMap.delete(id);
      }
    }
  }, [cursorsMap]);

  useEffect(() => {
    const timer = window.setInterval(flush, MAX_IDLE_TIME);
    const observer = () => {
      setCursors([...cursorsMap.values()]);
    };

    flush();
    setCursors([...cursorsMap.values()]);
    cursorsMap.observe(observer);

    return () => {
      cursorsMap.unobserve(observer);
      window.clearInterval(timer);
    };
  }, [flush]);

  return useMemo(
    () => cursors.filter(({ id }) => id !== cursorId),
    [cursors, cursorId],
  );
}
